import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Tiny floating-popover primitive used by Calendar.jsx's booking-card
 * popovers (details "i" + reassign-staff). The previous implementation
 * used `position: absolute` inside the booking-card container, which
 * has `overflow: hidden` for rounded corners AND is constrained to a
 * single Team-lane (~100-160px wide). The popover got clipped on
 * BOTH axes.
 *
 * This portals the content out to `document.body` with `position: fixed`,
 * anchored to the trigger button's `getBoundingClientRect()`. That
 * escapes every parent overflow / transform / z-index context. We
 * auto-flip horizontally if the popover would overflow the right
 * viewport edge, and vertically if it would overflow the bottom edge.
 *
 * Usage:
 *   <FloatingPopover
 *     trigger={(triggerProps) => <button {...triggerProps}><Icon /></button>}
 *     width={256}
 *     maxHeight={320}
 *     testId="booking-details-popover"
 *   >
 *     <div>… content …</div>
 *   </FloatingPopover>
 *
 * `width` and `maxHeight` are honoured directly so the parent can size
 * the popover; long lists get an internal scrollbar via maxHeight +
 * overflow-y-auto. On mobile widths the popover is automatically
 * shrunk to `viewport - 24px` so it never bleeds off-screen.
 */
export default function FloatingPopover({
  trigger,
  children,
  width = 280,
  maxHeight = 360,
  align = "end",         // "end" anchors popover's right edge to trigger's right edge
  testId,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, openUpward: false, width });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // Close on outside click + Escape. Pointer-down catches mobile taps too.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Position the popover relative to the trigger. Re-runs on scroll +
  // resize so the popover stays glued to the trigger as the user pans
  // the timeline. `useLayoutEffect` fires before paint → no flash.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    const recompute = () => {
      const r = triggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // On mobile widths, cap the popover width to viewport - 24px so it
      // can never bleed off-screen even if the caller asked for 320.
      const safeWidth = Math.min(width, vw - 24);
      // Horizontal: align to trigger's right edge by default ("end").
      // If that pushes the popover past the right viewport edge, snap
      // its right edge to viewport - 12px. Same for left overflow.
      let left = align === "start"
        ? r.left
        : r.right - safeWidth;
      left = Math.max(12, Math.min(left, vw - safeWidth - 12));
      // Vertical: open below the trigger by default. If not enough
      // space below (within 12px of viewport bottom), open upward.
      const spaceBelow = vh - r.bottom;
      const spaceAbove = r.top;
      const openUpward = spaceBelow < maxHeight + 24 && spaceAbove > spaceBelow;
      const top = openUpward
        ? Math.max(12, r.top - 8)         // popover bottom near trigger top
        : Math.min(vh - 12, r.bottom + 6); // popover top below trigger
      setPos({ top, left, openUpward, width: safeWidth });
    };
    recompute();
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    return () => {
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
    };
  }, [open, width, maxHeight, align]);

  const triggerProps = {
    ref: triggerRef,
    onClick: (e) => { e.stopPropagation(); setOpen((v) => !v); },
    "aria-expanded": open,
    "aria-haspopup": "dialog",
  };

  const popoverStyle = {
    top: pos.top,
    left: pos.left,
    width: pos.width,
    maxHeight,
    transform: pos.openUpward ? "translateY(-100%)" : "none",
  };

  return (
    <>
      {trigger(triggerProps)}
      {open && typeof document !== "undefined" && createPortal(
        <div
          ref={popoverRef}
          role="dialog"
          data-testid={testId}
          className={`fixed z-[1000] rounded-xl border border-border bg-card shadow-xl overflow-y-auto overscroll-contain ${className}`}
          style={popoverStyle}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Render-prop pattern so children can call onClose. */}
          {typeof children === "function" ? children({ close: () => setOpen(false) }) : children}
        </div>,
        document.body,
      )}
    </>
  );
}
